import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from './Checkbox';
import { DatePicker } from './DatePicker';
import ExperiencesField from './Experiences';
import { Form } from './Form';
import { PhoneInput } from './PhoneInput';
import { Radio, RadioOption } from './Radio';
import { Slider } from './Slider';
import { TextField } from './TextField';
import { VirtualizedAutocomplete } from './VirtualizedAutocomplete';
dayjs.extend(utc);

// Field types supported by the dynamic form
export type FieldType =
  | 'text'
  | 'email'
  | 'password'
  | 'select'
  | 'checkbox'
  | 'date'
  | 'phone'
  | 'number'
  | 'radio'
  | 'slider'
  | 'divider'
  | 'header'
  | 'experiences';


// Option for select fields
export interface SelectOption {
  value: string | number;
  label: string;
  icon?: string; // Optional icon, e.g., for country flags
}

// Option for slider fields
export interface SliderOption {
  value: number;
  label: string;
}

// Common field props
interface BaseFieldDefinition {
  type: FieldType;
  gridSize?: { xs?: number; sm?: number; md?: number; lg?: number };
  color?: string; // For custom styling
}

// Input field definition
interface InputFieldDefinition extends BaseFieldDefinition {
  type: 'text' | 'email' | 'password' | 'select' | 'checkbox' | 'date' | 'phone' | 'number' | 'radio' | 'slider' | 'experiences';
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  defaultValue?: any;
  options?: SelectOption[]; // For select fields and radio fields
  sliderOptions?: SliderOption[]; // For slider fields with custom labels
  min?: number; // For number fields and sliders
  max?: number; // For number fields and sliders
  step?: number; // For sliders
  hint?: string;
  convertValue?: (value: any) => any;
  onChange?: (value: any) => void;
  multiple?: boolean; // For select fields that allow multiple selections
  freeSolo?: boolean; // Allow free text entries in selects
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: SelectOption, state: any) => React.ReactNode;
  renderTags?: (value: SelectOption[], getTagProps: (params: { index: number }) => any) => React.ReactNode;
  isCountryField?: boolean; // Explicitly mark as a country field
  getCountryCodeFromOption?: (option: SelectOption) => string;
  leftLabel?: string; // For slider fields
  middleLabel?: string; // For slider fields
  rightLabel?: string; // For slider fields
  disabled?: boolean; // For disabling fields
  readonly?: boolean; // For read-only fields
  coerceType?: 'string' | 'date' | 'boolean' | 'number';
}

// Header field definition
interface HeaderFieldDefinition extends BaseFieldDefinition {
  type: 'header';
  name?: string;
  label: string;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
}

// Divider field definition
interface DividerFieldDefinition extends BaseFieldDefinition {
  type: 'divider';
  name?: string;
  label?: string;
}

// Field definition in the form schema
export type FormFieldDefinition = InputFieldDefinition | HeaderFieldDefinition | DividerFieldDefinition;

// Props for the DynamicForm component
export interface DynamicFormProps {
  fields: FormFieldDefinition[];
  onSubmit: (values: any) => void;
  initialValues?: Record<string, any>;
  submitLabel?: string;
  resetLabel?: string;
  onReset?: () => void;
  previousLabel?: string;
  onPrevious?: () => void;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  errors?: Record<string, string>;
  showActions?: boolean;
}


/**
 * DynamicForm component that renders a form based on a JSON schema
 */
export const DynamicForm = ({
  fields,
  onSubmit,
  initialValues = {},
  submitLabel = 'Submit',
  resetLabel = 'Reset',
  onReset,
  previousLabel = 'Previous',
  onPrevious,
  loading = false,
  title,
  subtitle,
  errors = {},
  showActions = true,
}: DynamicFormProps) => {
  const getNestedValue = useCallback((path: string, obj: Record<string, any>): any => {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === undefined || result === null) return undefined;
      result = result[key];
    }
    return result;
  }, []);

  const [fieldAdapters, setFieldAdapters] = useState<Record<string, any>>(() => {
    const adapters: Record<string, any> = {};

    fields.forEach(field => {
      // Skip adapter creation for UI-only elements
      if (field.type === 'divider' || field.type === 'header') {
        return;
      }

      // From this point, we know field is an InputFieldDefinition
      const inputField = field as InputFieldDefinition;
      const initialValue = inputField.name.includes('.')
        ? getNestedValue(inputField.name, initialValues)
        : initialValues[inputField.name];

      let defaultValue: any;
      if (inputField.type === 'experiences') {
        defaultValue = inputField.defaultValue || [];
      } else if (inputField.multiple) {
        defaultValue = [];
      } else {
        defaultValue = '';
      }

      adapters[inputField.name] = {
        state: { value: initialValue !== undefined ? initialValue : inputField.defaultValue || defaultValue },
        touched: false, // Track if field has been touched by user
        getMeta: () => ({ touchedErrors: errors[inputField.name] ? [errors[inputField.name]] : [] }),
        handleChange: (value: any) => {
          // Update only the specific field that changed and mark as touched
          setFieldAdapters(prev => ({
            ...prev,
            [inputField.name]: {
              ...prev[inputField.name],
              state: { value },
              touched: true
            }
          }));

          // Call the custom onChange handler if defined
          if (inputField.onChange) {
            inputField.onChange(value);
          }
        },
        handleBlur: () => { },
      };
    });

    return adapters;
  });

  // Update field adapters when fields or initialValues change
  useEffect(() => {
    setFieldAdapters(prev => {
      let hasChanges = false;
      const newAdapters: Record<string, any> = { ...prev };

      // Track current field names
      const currentFieldNames = new Set<string>();

      fields.forEach(field => {
        if (field.type === 'divider' || field.type === 'header') {
          return;
        }

        const inputField = field as InputFieldDefinition;
        currentFieldNames.add(inputField.name);

        const initialValue = inputField.name.includes('.')
          ? getNestedValue(inputField.name, initialValues)
          : initialValues[inputField.name];

        // Create missing adapters (e.g., when switching sections)
        if (!newAdapters[inputField.name]) {
          hasChanges = true;
          let defaultValue: any;
          if (inputField.type === 'experiences') {
            defaultValue = inputField.defaultValue || [];
          } else if (inputField.multiple) {
            defaultValue = [];
          } else {
            defaultValue = '';
          }

          newAdapters[inputField.name] = {
            state: { value: initialValue !== undefined ? initialValue : inputField.defaultValue || defaultValue },
            touched: false,
            getMeta: () => ({ touchedErrors: errors[inputField.name] ? [errors[inputField.name]] : [] }),
            handleChange: (value: any) => {
              setFieldAdapters(prevInner => ({
                ...prevInner,
                [inputField.name]: {
                  ...prevInner[inputField.name],
                  state: { value },
                  touched: true,
                },
              }));
              if (inputField.onChange) inputField.onChange(value);
            },
            handleBlur: () => { },
          };
          return;
        }

        // Update untouched fields when initial value changes
        if (
          initialValue !== undefined &&
          !newAdapters[inputField.name].touched &&
          newAdapters[inputField.name]?.state?.value !== initialValue
        ) {
          hasChanges = true;
          newAdapters[inputField.name] = {
            ...newAdapters[inputField.name],
            state: { value: initialValue },
          };
        }
      });

      // Remove adapters for fields no longer present
      Object.keys(newAdapters).forEach(name => {
        if (!currentFieldNames.has(name)) {
          hasChanges = true;
          delete newAdapters[name];
        }
      });

      return hasChanges ? newAdapters : prev;
    });
  }, [fields, initialValues, getNestedValue, errors]);

  // Collect form values
  const coerceValue = useCallback((value: any, coerceType?: InputFieldDefinition['coerceType']): any => {
    if (!coerceType) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(item => coerceValue(item, coerceType));
    }

    if (value === '' || value === null || value === undefined) {
      if (coerceType === 'string') {
        return value ?? '';
      }
      return undefined;
    }

    switch (coerceType) {
      case 'boolean':
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (normalized === 'true') return true;
          if (normalized === 'false') return false;
        }
        if (typeof value === 'number') {
          return value !== 0;
        }
        return Boolean(value);

      case 'number':
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return undefined;
          const parsed = Number(trimmed);
          return Number.isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
      case 'date':
        if (value instanceof Date) return value;
        if (typeof value === 'string') {
          const parsed = dayjs.utc(value);
          if (parsed.isValid()) {
            return parsed.toDate();
          }
        }
        return undefined;

      case 'string':
      default:
        return String(value);
    }
  }, []);

  const getFormValues = useCallback(() => {
    const values: Record<string, any> = {};

    fields.forEach(field => {
      // Skip UI-only elements
      if (field.type === 'divider' || field.type === 'header') {
        return;
      }

      // From this point, we know field is an InputFieldDefinition
      const inputField = field as InputFieldDefinition;
      const value = fieldAdapters[inputField.name].state.value;
      const coercedValue = coerceValue(value, inputField.coerceType);

      // Handle nested fields
      if (inputField.name.includes('.')) {
        const parts = inputField.name.split('.');
        let current = values;

        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (part) {
            if (!current[part]) {
              current[part] = {};
            }
            current = current[part];
          }
        }

        const lastPart = parts[parts.length - 1];
        if (lastPart) {
          current[lastPart] = coercedValue;
        }
      } else {
        values[inputField.name] = coercedValue;
      }
    });

    return values;
  }, [fields, fieldAdapters, coerceValue]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(getFormValues());
  }, [onSubmit, getFormValues]);

  // Check if a field is a country selection field
  const isCountryField = useMemo(() => {
    return (field: FormFieldDefinition) => {
      if (field.type === 'divider' || field.type === 'header') {
        return false;
      }

      // From this point, we know field is an InputFieldDefinition
      const inputField = field as InputFieldDefinition;

      // Explicitly marked as country field
      if (inputField.isCountryField) return true;

      // Check if the field name suggests it's a country field
      const countryFieldNames = ['country', 'citizenship', 'nationality', 'nation'];
      const fieldNameLower = inputField.name.toLowerCase();

      return countryFieldNames.some(name => fieldNameLower.includes(name)) &&
        inputField.type === 'select' &&
        inputField.options &&
        inputField.options.length > 10 && // Likely a country list if it has many options
        inputField.options.some((opt: SelectOption) => typeof opt.value === 'string' && opt.value.length === 2); // Country codes are typically 2 chars
    };
  }, []);

  // Convert field adapter to required format for VirtualizedAutocomplete
  const getVirtualizedAutocompleteProps = useCallback((field: InputFieldDefinition, fieldAdapter: any) => {
    const options = field.options || [];

    // For multiple select, convert value to array of option objects
    const getValue = () => {
      const value = field.convertValue ? field.convertValue(fieldAdapter.state.value) : fieldAdapter.state.value;

      if (field.multiple) {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return [];
        }

        return value.map(v =>
          options.find((opt: SelectOption) => opt.value === v) || { value: v, label: String(v) }
        );
      }

      // For single select, return the matching option object or null
      if (!value) {
        return null;
      }

      return options.find((opt: SelectOption) => opt.value === value) || null;
    };

    // Handle change event
    const handleChange = (_: React.SyntheticEvent, newValue: any) => {
      let processedValue;

      if (field.multiple && Array.isArray(newValue)) {
        processedValue = newValue.map((item: any) =>
          typeof item === 'string' ? item : item.value
        );
      } else if (newValue === null) {
        processedValue = '';
      } else if (typeof newValue === 'object') {
        processedValue = newValue.value;
      } else {
        processedValue = newValue;
      }

      fieldAdapter.handleChange(processedValue);
    };

    const isCountrySelector = isCountryField(field);

    return {
      field: fieldAdapter,
      name: field.name,
      textFieldProps: {
        label: field.label,
        placeholder: field.placeholder,
        helperText: field.helperText,
        required: field.required
      },
      options: options,
      isCountrySelector,
      getCountryCodeFromOption: field.getCountryCodeFromOption,
      multiple: field.multiple,
      value: getValue(),
      onChange: handleChange,
      renderOption: field.renderOption,
      renderTags: field.renderTags,
      required: field.required
    };
  }, [isCountryField]);

  // Memoized field components to prevent unnecessary re-renders
  const MemoizedTextField = useMemo(() => React.memo(TextField), []);
  const MemoizedVirtualizedAutocomplete = useMemo(() => React.memo(VirtualizedAutocomplete), []);
  const MemoizedCheckbox = useMemo(() => React.memo(Checkbox), []);
  const MemoizedDatePicker = useMemo(() => React.memo(DatePicker), []);
  const MemoizedPhoneInput = useMemo(() => React.memo(PhoneInput), []);
  const MemoizedRadio = useMemo(() => React.memo(Radio), []);
  const MemoizedExperiencesField = useMemo(() => React.memo(ExperiencesField), []);
  const MemoizedSlider = useMemo(() => React.memo(Slider), []);

  // Render a field based on its type
  const renderField = useCallback((field: FormFieldDefinition) => {
    // Special handlers for UI-only elements
    if (field.type === 'divider') {
      return (
        <Box
          sx={{
            width: '100%',
            my: 2,
            borderBottom: '1px solid',
            borderColor: field.color || 'divider'
          }}
        />
      );
    }

    if (field.type === 'header') {
      return (
        <Typography
          variant={field.variant || 'h6'}
          sx={{
            mt: 3,
            mb: 1,
            color: field.color || 'text.primary',
            width: '100%'
          }}
        >
          {field.label}
        </Typography>
      );
    }

    const fieldAdapter = fieldAdapters[field.name];
    if (!fieldAdapter) return null;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'number':
        return (
          <MemoizedTextField
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            type={field.type}
            placeholder={field.placeholder}
            helperText={field.helperText}
            required={field.required}
            disabled={field.disabled}
            InputProps={field.readonly ? { readOnly: true } : undefined}
          />
        );

      case 'select':
        const autocompleteProps = getVirtualizedAutocompleteProps(field as InputFieldDefinition, fieldAdapter);
        return (
          <MemoizedVirtualizedAutocomplete
            {...autocompleteProps}
            freeSolo={(field as InputFieldDefinition).freeSolo}
          />
        );

      case 'checkbox':
        return (
          <MemoizedCheckbox
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            helperText={field.helperText}
          />
        );

      case 'date':
        return (
          <MemoizedDatePicker
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            helperText={field.helperText}
          />
        );

      case 'phone':
        return (
          <MemoizedPhoneInput
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
          />
        );

      case 'radio':
        // Convert SelectOption array to RadioOption array (ensuring string values)
        const radioOptions: RadioOption[] = (field.options || []).map(opt => ({
          value: String(opt.value),
          label: opt.label
        }));

        return (
          <MemoizedRadio
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            options={radioOptions}
            helperText={field.helperText}
            required={field.required}
          />
        );

      case 'experiences':
        return (
          <MemoizedExperiencesField
            field={fieldAdapter}
            name={field.name}
            label={field.label}
            helperText={field.helperText}
            required={field.required}
          />
        );

      case 'slider':
        const sliderField = field as InputFieldDefinition;

        return (
          <MemoizedSlider
            field={fieldAdapter}
            name={sliderField.name}
            label={sliderField.label}
            helperText={sliderField.helperText}
            required={sliderField.required}
            min={sliderField.min || 0}
            max={sliderField.max || 10}
            step={sliderField.step || 1}
            leftLabel={sliderField.leftLabel}
            middleLabel={sliderField.middleLabel}
            rightLabel={sliderField.rightLabel}
          />
        );

      default:
        return null;
    }
  }, [fieldAdapters, getVirtualizedAutocompleteProps, MemoizedTextField, MemoizedVirtualizedAutocomplete, MemoizedCheckbox, MemoizedDatePicker, MemoizedPhoneInput, MemoizedRadio, MemoizedExperiencesField, MemoizedSlider]);

  // Memoize the field rendering to prevent unnecessary re-renders
  const memoizedFields = useMemo(() => {
    return fields.map((field, index) => (
      <Grid
        key={field.type === 'divider' || field.type === 'header' ? `${field.type}-${index}` : ('name' in field ? field.name : `ui-element-${index}`)}
        container={false}
        size={field.gridSize || { xs: 12 }}
      >
        {renderField(field)}
      </Grid>
    ));
  }, [fields, renderField]);

  return (
    <Box>
      {title && (
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {title}
        </Typography>
      )}

      {subtitle && (
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
          {subtitle}
        </Typography>
      )}

      <Form
        onSubmit={handleSubmit}
        onReset={onReset}
        previousLabel={previousLabel}
        onPrevious={onPrevious}
        loading={loading}
        submitLabel={submitLabel}
        resetLabel={resetLabel}
        showActions={showActions}
      >
        <Grid container spacing={2}>
          {memoizedFields}
        </Grid>

        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
      </Form>
    </Box>
  );
};
